import { get, post, put, del, patch, download, upload } from "./lewooApi";

export const erpGet = async (path, params = {}) => {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") qs.set(k, v);
  });
  const q = qs.toString();
  const res = await get(`/api${path}${q ? `?${q}` : ""}`);
  return res?.data;
};

export const erpPost = async (path, body) => {
  const res = await post(`/api${path}`, body);
  return res?.data;
};

export const erpPut = async (path, body) => {
  const res = await put(`/api${path}`, body);
  return res?.data;
};

export const erpDel = async (path) => {
  const res = await del(`/api${path}`);
  return res?.data;
};

export const erpPatch = async (path, body) => {
  const res = await patch(`/api${path}`, body);
  return res?.data;
};

export const erpExport = async (params = {}) => {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") qs.set(k, v);
  });
  const q = qs.toString();
  const kind = params.kind || "report";
  await download(`/api/reports/export${q ? `?${q}` : ""}`, `${kind}.${params.format || "xlsx"}`);
};

function referential(path) {
  return {
    list: (params) => erpGet(path, params),
    get: (id) => erpGet(`${path}/${id}`),
    create: (body) => erpPost(path, body),
    update: (id, body) => erpPut(`${path}/${id}`, body),
    remove: (id) => erpDel(`${path}/${id}`),
  };
}

export const api = {
  partners: {
    list: (params) => erpGet("/partners", { ...params, limit: params?.limit || 1000 }),
    get: (id) => erpGet(`/partners/${id}`),
    create: (body) => erpPost("/partners", body),
    update: (id, body) => erpPut(`/partners/${id}`, body),
    remove: (id) => erpDel(`/partners/${id}`),
    export: (params = {}) => {
      const { format = "xlsx", ...filters } = params;
      const qs = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== "") qs.set(k, v);
      });
      qs.set("format", format);
      return download(`/api/partners/export?${qs.toString()}`, `partners.${format}`);
    },
    stats: {
      activeSummary: () => erpGet("/partners/stats/active-summary"),
    },
  },
  users: {
    list: () => erpGet("/users", { limit: 500 }),
  },
  products: {
    list: (params) => erpGet("/products", params),
    get: (id) => erpGet(`/products/${id}`),
    create: (body) => erpPost("/products", body),
    update: (id, body) => erpPut(`/products/${id}`, body),
    remove: (id) => erpDel(`/products/${id}`),
    categories: () => erpGet("/products/categories", { limit: 500 }),
    uoms: () => erpGet("/products/uom", { limit: 500 }),
    taxes: () => erpGet("/products/taxes", { limit: 500 }),
  },
  sales: {
    list: (params) => erpGet("/sales-orders", params),
    get: (id) => erpGet(`/sales-orders/${id}`),
    lines: (id) => erpGet(`/sales-orders/${id}/lines`),
    create: (body) => erpPost("/sales-orders", body),
    update: (id, body) => erpPut(`/sales-orders/${id}`, body),
    confirm: (id, body = {}) => erpPost(`/sales-orders/${id}/confirm`, body),
    cancel: (id, body = {}) => erpPost(`/sales-orders/${id}/cancel`, body),
    invoice: (id, body = {}) => erpPost(`/sales-orders/${id}/invoice`, body),
    deliver: (id, body) => erpPost(`/sales-orders/${id}/deliver`, body),
    subscriptionRenew: (id, body) => erpPost(`/sales-orders/${id}/subscription-renew`, body),
    portalPreview: (id) => erpGet(`/sales-orders/${id}/portal`),
    expenses: (id, params) => erpGet(`/sales-orders/${id}/expenses`, params),
    remove: (id) => erpDel(`/sales-orders/${id}`),
    invoices: (params) => erpGet("/sales/invoices", params),
    dashboard: () => erpGet("/sales/dashboard"),
    sendEmail: (id, body) => erpPost(`/sales-orders/${id}/send-email`, body),
    sendWhatsApp: (id, body) => erpPost(`/sales-orders/${id}/send-whatsapp`, body),
    emailHistory: (id, params) => erpGet(`/sales-orders/${id}/email-history`, params),
    contracts: {
      expired: (params) => erpGet("/sales-orders/contracts/expired", params),
      markRenewed: (id, body) => erpPost(`/sales-orders/${id}/mark-renewed`, body),
      alerts: () => erpGet("/sales-orders/contracts/alerts"),
    },
  },
  purchase: {
    list: (params) => erpGet("/purchase-orders", params),
    get: (id) => erpGet(`/purchase-orders/${id}`),
    lines: (id) => erpGet(`/purchase-orders/${id}/lines`),
    create: (body) => erpPost("/purchase-orders", body),
    update: (id, body) => erpPut(`/purchase-orders/${id}`, body),
    confirm: (id) => erpPost(`/purchase-orders/${id}/confirm`),
    receive: (id) => erpPost(`/purchase-orders/${id}/receive`),
    vendorBill: (id, body) => erpPost(`/purchase-orders/${id}/vendor-bill`, body),
    vendorBills: (params) => erpGet("/purchases/vendor-bills", params),
    cancel: (id) => erpPost(`/purchase-orders/${id}/cancel`),
    remove: (id) => erpDel(`/purchase-orders/${id}`),
    dashboard: () => erpGet("/purchases/dashboard"),
  },
  inventory: {
    stock: (params) => erpGet("/inventory/stock", params),
    moves: (params) => erpGet("/inventory/moves", params),
    adjustments: (body) => erpPost("/inventory/adjustments", body),
    receive: (body) => erpPost("/inventory/receive", body),
    transfer: (body) => erpPost("/inventory/transfer", body),
    undo: (id) => erpPost(`/inventory/${id}/undo`),
    revalue: (id, body) => erpPost(`/inventory/${id}/revalue`, body),
    warehouses: () => erpGet("/inventory/warehouses", { limit: 500 }),
    locations: (params) => erpGet("/inventory/locations", params),
    dashboard: () => erpGet("/inventory/dashboard"),
  },
  accounts: {
    list: (params) => erpGet("/accounts", params),
    types: () => erpGet("/accounts/types", { limit: 500 }),
    get: (id) => erpGet(`/accounts/${id}`),
    create: (body) => erpPost("/accounts", body),
    update: (id, body) => erpPut(`/accounts/${id}`, body),
    remove: (id) => erpDel(`/accounts/${id}`),
    unreconciled: (id) => erpGet(`/accounts/${id}/unreconciled`),
  },
  journals: {
    list: (params) => erpGet("/journals", params),
    get: (id) => erpGet(`/journals/${id}`),
    create: (body) => erpPost("/journals", body),
    update: (id, body) => erpPut(`/journals/${id}`, body),
    remove: (id) => erpDel(`/journals/${id}`),
  },
  accounting: {
    moves: (params) => erpGet("/account-moves", params),
    get: (id) => erpGet(`/account-moves/${id}`),
    create: (body) => erpPost("/account-moves", body),
    update: (id, body) => erpPut(`/account-moves/${id}`, body),
    post: (id, body = {}) => erpPost(`/account-moves/${id}/post`, body),
    cancel: (id, body = {}) => erpPost(`/account-moves/${id}/cancel`, body),
    duplicate: (id, body = {}) => erpPost(`/account-moves/${id}/duplicate`, body),
    creditNote: (id, body) => erpPost(`/account-moves/${id}/credit-note`, body),
    refund: (body) => erpPost(`/account-moves`, { ...body, move_type: "out_refund" }),
    pay: (id, body) => erpPost(`/account-moves/${id}/pay`, body),
    reconcile: (id, body) => erpPost(`/account-moves/${id}/reconcile`, body),
    receivables: (params) => erpGet("/receivables", params),
    payables: (params) => erpGet("/payables", params),
    vendorBills: (params) => erpGet("/purchases/vendor-bills", params),
    periods: (params) => erpGet("/account-periods", params),
    closePeriod: (id) => erpPost(`/account-periods/${id}/close`),
    openPeriod: (id) => erpPost(`/account-periods/${id}/open`),
    generalLedger: (params) => erpGet("/general-ledger", params),
    trialBalance: (params) => erpGet("/general-ledger/trial-balance", params),
  },
  payments: {
    list: (params) => erpGet("/payments", params),
    get: (id) => erpGet(`/payments/${id}`),
    create: (body) => erpPost("/payments", body),
    cancel: (id) => erpPost(`/payments/${id}/cancel`),
  },
  expenses: {
    list: (params) => erpGet("/expenses", params),
    get: (id) => erpGet(`/expenses/${id}`),
    create: (body) => erpPost("/expenses", body),
    update: (id, body) => erpPut(`/expenses/${id}`, body),
  },
  salaryAdvances: {
    list: (params) => erpGet("/salary-advances", params),
    get: (id) => erpGet(`/salary-advances/${id}`),
    create: (body) => erpPost("/salary-advances", body),
    update: (id, body) => erpPut(`/salary-advances/${id}`, body),
  },
  reports: {
    profitLoss: (params) => erpGet("/reports/profit-loss", params),
    balanceSheet: (params) => erpGet("/reports/balance-sheet", params),
    taxes: (params) => erpGet("/reports/taxes", params),
    stockValuation: (params) => erpGet("/reports/stock-valuation", params),
  },
  crm: {
    // Leads (CRUD + filtres riches)
    list: (params) => erpGet("/crm-leads", params),
    get: (id) => erpGet(`/crm-leads/${id}`),
    create: (body) => erpPost("/crm-leads", body),
    update: (id, body) => erpPut(`/crm-leads/${id}`, body),
    remove: (id) => erpDel(`/crm-leads/${id}`),

    // Actions métier
    convert: (id) => erpPost(`/crm-leads/${id}/convert`),
    assign: (id, body) => erpPost(`/crm-leads/${id}/assign`, body),
    won: (id, body) => erpPost(`/crm-leads/${id}/won`, body),
    lost: (id, body) => erpPost(`/crm-leads/${id}/lost`, body),
    createQuotation: (id, body) => erpPost(`/crm-leads/${id}/create-quotation`, body),
    quotationPreview: (id) => erpGet(`/crm-leads/${id}/quotation-preview`),
    sendEmail: (id, body) => erpPost(`/crm-leads/${id}/send-email`, body),
    sendSms: (id, body) => erpPost(`/crm-leads/${id}/send-sms`, body),
    sendChatRoom: (id, body) => erpPost(`/crm-leads/${id}/send-chatroom`, body),
    follow: (id, body) => erpPost(`/crm-leads/${id}/follow`, body),
    unfollow: (id) => erpDel(`/crm-leads/${id}/follow`),
    merge: (id, body) => erpPost(`/crm-leads/${id}/merge`, body),
    history: (id) => erpGet(`/crm-leads/${id}/history`),

    // Messages / notes
    messages: (id) => erpGet(`/crm-leads/${id}/messages`),
    addMessage: (id, body) => erpPost(`/crm-leads/${id}/messages`, body),
    updateMessage: (id, messageId, body) => erpPut(`/crm-leads/${id}/messages/${messageId}`, body),
    removeMessage: (id, messageId) => erpDel(`/crm-leads/${id}/messages/${messageId}`),

    // Pièces jointes
    attachments: (id) => erpGet(`/crm-leads/${id}/attachments`),
    addAttachment: (id, body) => erpPost(`/crm-leads/${id}/attachments`, body),
    downloadAttachment: (id, attachmentId, filename) => download(`/api/crm-leads/${id}/attachments/${attachmentId}/download`, filename),
    removeAttachment: (id, attachmentId) => erpDel(`/crm-leads/${id}/attachments/${attachmentId}`),

    // Référentiels (CRUD commun)
    stages: {
      ...referential("/crm-stages"),
      reorder: (body) => erpPost("/crm-stages/reorder", body),
    },
    teams: {
      ...referential("/crm-teams"),
      addMember: (id, body) => erpPost(`/crm-teams/${id}/members`, body),
      removeMember: (id, userId) => erpDel(`/crm-teams/${id}/members/${userId}`),
    },
    tags: referential("/crm-tags"),
    groups: {
      ...referential("/crm-groups"),
      leads: (id, params) => erpGet(`/crm-groups/${id}/leads`, params),
      addLeads: (id, body) => erpPost(`/crm-groups/${id}/leads`, body),
      setLeads: (id, body) => erpPut(`/crm-groups/${id}/leads`, body),
      removeLead: (id, leadId) => erpDel(`/crm-groups/${id}/leads/${leadId}`),
    },
    lostReasons: referential("/crm-lost-reasons"),
    activityTypes: referential("/crm-activity-types"),
    sources: referential("/crm-sources"),
    mediums: referential("/crm-mediums"),
    campaigns: referential("/crm-campaigns"),

    // Activités
    activities: {
      list: (params) => erpGet("/crm-activities", params),
      get: (id) => erpGet(`/crm-activities/${id}`),
      create: (body) => erpPost("/crm-activities", body),
      update: (id, body) => erpPut(`/crm-activities/${id}`, body),
      remove: (id) => erpDel(`/crm-activities/${id}`),
      done: (id) => erpPatch(`/crm-activities/${id}/done`),
      snooze: (id, body) => erpPatch(`/crm-activities/${id}/snooze`, body),
    },

    // Reporting
    dashboard: () => erpGet("/crm/dashboard"),
    forecast: (params) => erpGet("/crm/forecast", params),
    teamPerformance: (params) => erpGet("/crm/team-performance", params),
    pipelineByStage: (params) => erpGet("/crm/pipeline-by-stage", params),
    export: (params = {}) => {
      const { format = "xlsx", ...filters } = params;
      const qs = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== "") qs.set(k, v);
      });
      qs.set("format", format);
      return download(`/api/crm/export?${qs.toString()}`, `crm-leads.${format}`);
    },

    leadProducts: {
      list: (leadId, params) => erpGet(`/crm-leads/${leadId}/products`, params),
      add: (leadId, body) => erpPost(`/crm-leads/${leadId}/products`, body),
      update: (leadId, productId, body) => erpPut(`/crm-leads/${leadId}/products/${productId}`, body),
      remove: (leadId, productId) => erpDel(`/crm-leads/${leadId}/products/${productId}`),
    },
  },
  mail: {
    variables: () => erpGet("/mail/variables"),
    bulk: (body) => erpPost("/mail/bulk", body),
    smtp: {
      get: () => erpGet("/mail/smtp"),
      update: (body) => erpPut("/mail/smtp", body),
      verify: () => erpPost("/mail/smtp/verify"),
      test: (to) => erpPost("/mail/smtp/test", { to }),
    },
    templates: referential("/mail/templates"),
    rules: {
      ...referential("/mail/rules"),
      list: async (params = {}) => {
        const qs = new URLSearchParams();
        Object.entries(params).forEach(([k, v]) => {
          if (v !== undefined && v !== null && v !== "") qs.set(k, v);
        });
        const q = qs.toString();
        const res = await get(`/api/mail/rules${q ? `?${q}` : ""}`);
        const raw = res && typeof res === "object" ? res : {};
        const rows = Array.isArray(raw?.data) ? raw.data : Array.isArray(raw) ? raw : [];
        const changedFieldsAllowed =
          raw?.changed_fields_allowed ??
          raw?.meta?.changed_fields_allowed ??
          rows.find((r) => r && typeof r === "object" && Array.isArray(r.changed_fields_allowed))?.changed_fields_allowed ??
          null;
        return { rows, changedFieldsAllowed };
      },
    },
    outbox: {
      list: async (params = {}) => {
        const qs = new URLSearchParams();
        Object.entries(params).forEach(([k, v]) => {
          if (v !== undefined && v !== null && v !== "") qs.set(k, v);
        });
        const q = qs.toString();
        const res = await get(`/api/mail/outbox${q ? `?${q}` : ""}`);
        return { rows: res?.data || [], stats: res?.stats || [] };
      },
      get: (uid) => erpGet(`/mail/outbox/${uid}`),
      retry: (uid) => erpPost(`/mail/outbox/${uid}/retry`),
      remove: (uid) => erpDel(`/mail/outbox/${uid}`),
    },
    whatsapp: {
      transport: (typeof window !== "undefined" && window.__WA_TRANSPORT__) || "v2",
      devices: {
        list: () => erpGet("/whatsapp/devices"),
        create: (body) => erpPost("/whatsapp/devices", body),
        update: (id, body) => erpPut(`/whatsapp/devices/${id}`, body),
        remove: (id) => erpDel(`/whatsapp/devices/${id}`),
        connect: (id) => {
          const t = api.mail.whatsapp.transport;
          return t === "v2" ? erpPost(`/whatsapp-v2/devices/${id}/connect`) : erpPost(`/whatsapp/devices/${id}/connect`);
        },
        disconnect: (id) => {
          const t = api.mail.whatsapp.transport;
          return t === "v2" ? erpPost(`/whatsapp-v2/devices/${id}/disconnect`) : erpPost(`/whatsapp/devices/${id}/disconnect`);
        },
        qr: (id) => {
          const t = api.mail.whatsapp.transport;
          return t === "v2" ? erpGet(`/whatsapp-v2/devices/${id}/qr`) : erpGet(`/whatsapp/devices/${id}/qr`);
        },
        status: (id) => erpGet(`/whatsapp-v2/devices/${id}/status`),
      },
      test: (body) => {
        const t = api.mail.whatsapp.transport;
        return t === "v2" ? erpPost("/whatsapp-v2/test", body) : erpPost("/whatsapp/test", body);
      },
      // Envoi direct synchrone avec PJ : POST /whatsapp-v2/test
      // {to, body?, device_id?, attachments?: [{file_path, file_name, mime_type}], media_url?}
      // -> {messageId, mediaSent, device_id, to_phone}. Immediat, sans file d'attente.
      direct: (body) => erpPost("/whatsapp-v2/test", body),
      inbox: {
        list: (params) => erpGet("/whatsapp/inbox", params),
        get: (id) => erpGet(`/whatsapp/inbox/${id}`),
        remove: (id) => erpDel(`/whatsapp/inbox/${id}`),
        unreadCount: () => erpGet("/whatsapp/inbox/unread-count"),
        markRead: (id) => erpPut(`/whatsapp/inbox/${id}/read`),
        markAllRead: () => erpPut("/whatsapp/inbox/read-all"),
      },
      stats: () => erpGet("/mail/stats"),
      statsChannel: () => erpGet("/whatsapp/stats"),
      health: () => erpGet("/whatsapp/health"),
      v2: {
        connect: (id) => erpPost(`/whatsapp-v2/devices/${id}/connect`),
        qr: (id) => erpGet(`/whatsapp-v2/devices/${id}/qr`),
        disconnect: (id) => erpPost(`/whatsapp-v2/devices/${id}/disconnect`),
        status: (id) => erpGet(`/whatsapp-v2/devices/${id}/status`),
        test: (body) => erpPost("/whatsapp-v2/test", body),
      },
    },
  },
  upload: (file, extra) => upload("/api/uploads", file, extra),
};

// Alias pour rétro-compatibilité : ancien code utilisait api.whatsapp au lieu de api.mail.whatsapp
api.whatsapp = api.mail.whatsapp;

export const fmt = {
  money: (v) =>
    (Number(v) || 0).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + " FCFA",
  moneyShort: (v) => {
    const n = Number(v) || 0;
    if (n >= 1000000) return (n / 1000000).toFixed(1).replace(".0", "") + "M";
    if (n >= 1000) return (n / 1000).toFixed(0) + "k";
    return String(n);
  },
  qty: (v) => {
    const n = Number(v) || 0;
    return Number.isInteger(n) ? String(n) : n.toFixed(2).replace(/\.?0+$/, "");
  },
  date: (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    if (isNaN(d)) return iso;
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  },
  dateTime: (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    if (isNaN(d)) return iso;
    return d.toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  },
};

export const STATE_LABELS = {
  draft: "Draft",
  sent: "Sent",
  sale: "Sale",
  purchase: "Purchase",
  done: "Done",
  cancel: "Cancelled",
  invoiced: "Invoiced",
  paid: "Paid",
};

export const STATE_COLORS = {
  draft: { text: "#64748b", bg: "#f1f5f9" },
  sent: { text: "#b45309", bg: "#fffbeb" },
  sale: { text: "#225f2b", bg: "#f0f7f0" },
  purchase: { text: "#225f2b", bg: "#f0f7f0" },
  done: { text: "#0f766e", bg: "#eef6f3" },
  cancel: { text: "#be123c", bg: "#fff1f2" },
  invoiced: { text: "#225f2b", bg: "#f0f7f0" },
  paid: { text: "#0f766e", bg: "#eef6f3" },
};

export function stateBadgeStyle(state) {
  const key = STATE_LABELS[state] ? state : "draft";
  return STATE_COLORS[key] || STATE_COLORS.draft;
}

export const stateBadgeStyleObj = (state) => {
  const key = STATE_LABELS[state] ? state : "draft";
  const c = STATE_COLORS[key] || STATE_COLORS.draft;
  return { background: c.bg, color: c.text, padding: "3px 10px", borderRadius: "6px", fontSize: "12px", fontWeight: "500", whiteSpace: "nowrap", display: "inline-block" };
};
